import { Injectable } from "@angular/core";
import { RtmpDestinationV2Entity } from "@common/domain";
import { DESTINATION_CONFIRM_MESSAGE } from "@youm-front/destinations/domain";
import { createEffect, Actions, ofType } from "@ngrx/effects";
import { fetch } from "@nrwl/angular";
import { ConfirmService } from "@youm-front/ui";
import { of } from "rxjs";
import { map, mergeMap, switchMap } from "rxjs/operators";
import { DestinationsActions } from "../actions";
import {
  RtmpDestinationCreateRequestDTO,
  RtmpDestinationDeleteRequestDTO,
  RtmpDestinationUpdateRequestDTO
} from "../dto";
import { DestinationsService } from "../service";

@Injectable()
export class DestinationsEffects {
  loadRtmpDestinations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DestinationsActions.loadRtmpDestinations),
      fetch({
        run: (_action) => {
          return this.destinationsService.rtmpDestinationsList().pipe(
            map((_) =>
              DestinationsActions.loadRtmpDestinationsSuccess({
                destinations: _.value.destinations.map(
                  (_) => new RtmpDestinationV2Entity(_)
                )
              })
            )
          );
        },
        onError: (action, error) =>
          DestinationsActions.loadRtmpDestinationsFailure({ error })
      })
    )
  );

  addRtmpDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DestinationsActions.addRtmpDestination),
      fetch({
        run: ({ url, key, roomId }) => {
          return this.destinationsService
            .rtmpDestinationCreate(
              new RtmpDestinationCreateRequestDTO({
                url,
                key,
                roomId
              })
            )
            .pipe(
              map((_) =>
                DestinationsActions.addRtmpDestinationSuccess({
                  destination: new RtmpDestinationV2Entity(_.value.destination)
                })
              )
            );
        },
        onError: (action, error) =>
          DestinationsActions.addRtmpDestinationFailure({ error })
      })
    )
  );

  updateRtmpDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DestinationsActions.updateRtmpDestination),
      fetch({
        run: ({ id, key, url, roomId }) => {
          return this.confirmService
            .confirm(DESTINATION_CONFIRM_MESSAGE.onUpdate)
            .pipe(
              switchMap((confirmed) =>
                confirmed
                  ? this.destinationsService
                      .rtmpDestinationUpdate(
                        new RtmpDestinationUpdateRequestDTO({
                          id,
                          url,
                          key,
                          roomId
                        })
                      )
                      .pipe(
                        mergeMap((_) => [
                          DestinationsActions.updateRtmpDestinationSuccess({
                            destination: new RtmpDestinationV2Entity(
                              _.value.destination
                            )
                          })
                        ])
                      )
                  : of(DestinationsActions.updateRtmpDestinationCancel())
              )
            );
        },
        onError: (action, error) =>
          DestinationsActions.updateRtmpDestinationFailure({
            error: error.message || error
          })
      })
    )
  );

  deleteDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DestinationsActions.deleteRtmpDestination),
      fetch({
        run: (action) => {
          return this.confirmService
            .confirm(DESTINATION_CONFIRM_MESSAGE.onDelete)
            .pipe(
              switchMap((confirmed) =>
                confirmed
                  ? this.destinationsService
                      .rtmpDestinationDelete(
                        new RtmpDestinationDeleteRequestDTO({ id: action.id })
                      )
                      .pipe(
                        map(() =>
                          DestinationsActions.deleteRtmpDestinationSuccess({
                            id: action.id
                          })
                        )
                      )
                  : of(DestinationsActions.deleteRtmpDestinationCancel())
              )
            );
        },
        onError: (action, error) =>
          DestinationsActions.deleteRtmpDestinationFailure({ error })
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly destinationsService: DestinationsService,
    private readonly confirmService: ConfirmService
  ) {}
}
